import { Box, Divider, Stack, Typography, useTheme } from '@mui/material';
import { generateClient } from 'aws-amplify/api';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Schema } from '../../../../amplify/data/resource';
import Button from '../../../components/custom/Button';
import Tabs from '../../../components/custom/Tabs';
import TextField from '../../../components/custom/TextField';
import { formatIsraeliPhoneNumber } from '../../../utils/format/format';

const apiClient = generateClient<Schema>();

interface FormValues {
  amount: number;
  targetName: string;
  targetBankAccount: string;
  phoneNumber: string;
  userId: string;
  companyId?: string;
  debtorBankAccount?: string;
}

const Feezback = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const {
    handleSubmit,
    control,
    setValue,
    reset,
    setError,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      targetName: 'PielaPay',
      targetBankAccount: '20-532-227738',
    },
  });

  const [selectedDebtorAccountType, setSelectedDebtorAccountType] = useState<
    'INDIVIDUAL' | 'BUSINESS'
  >('BUSINESS');

  const myTabs = [
    { label: 'BUSINESS', content: 'BUSINESS' as 'INDIVIDUAL' | 'BUSINESS' },
    { label: 'INDIVIDUAL', content: 'INDIVIDUAL' as 'INDIVIDUAL' | 'BUSINESS' },
  ];

  const handleTabChange = (index: number) => {
    setSelectedDebtorAccountType(myTabs[index].content);
  };

  const onSubmit = async (formData: FormValues) => {
    try {
      setLoading(true);

      const formattedPhoneNumber = formatIsraeliPhoneNumber(
        formData.phoneNumber,
      );

      const response = await apiClient.queries.generatePaymentLink({
        amount: Number(formData.amount),
        fundName: String(formData.targetName),
        targetBankAccount: String(formData.targetBankAccount),
        clientPhoneNumber: formattedPhoneNumber,
        userId: String(formData.userId),
        context: 'custom',
        sendMessage: true,
        companyId: String(formData.companyId),
        debtorAccountType: selectedDebtorAccountType,
        debtorBankAccount: String(formData.debtorBankAccount),
      });

      if (response?.data) {
        const parsedData =
          typeof response.data === 'string'
            ? JSON.parse(response.data)
            : response.data;

        if (parsedData.statusCode === 200) {
          enqueueSnackbar('לינק נשלח בהצלחה', { variant: 'success' });
        } else {
          enqueueSnackbar('אירעה תקלה', { variant: 'error' });
        }
      } else {
        enqueueSnackbar('אירעה תקלה', { variant: 'error' });
      }
      setLoading(false);
    } catch (error) {
      enqueueSnackbar('אירעה תקלה', { variant: 'error' });
      console.error('Failed to submit contact:', error);
    }
  };

  return (
    <Stack
      component="form"
      spacing={2}
      width={'400px'}
      alignItems={'flex-end'}
      sx={{ ml: 'auto' }}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Typography fontSize="18px" fontWeight="700" fontFamily={'AlmoniDL'} mb={2} textAlign="right">F</Typography>
      <Controller
        name="amount"
        control={control}
        rules={{ required: 'חובה למלא את השדה' }}
        render={({ field }) => (
          <TextField
            {...field}
            label="סכום"
            fullWidth
            required
            error={!!errors.amount}
            value={field.value?.toString() || ''}
            helperText={errors.amount?.message}
          />
        )}
      />
      <Controller
        name="userId"
        control={control}
        rules={{ required: 'חובה למלא את השדה' }}
        render={({ field }) => (
          <TextField
            {...field}
            label="ת״ז"
            fullWidth
            required
            error={!!errors.userId}
            value={field.value?.toString() || ''}
            helperText={errors.userId?.message}
          />
        )}
      />
      <Controller
        name="companyId"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label="ח.פ"
            fullWidth
            error={!!errors.companyId}
            value={field.value?.toString() || ''}
            helperText={errors.companyId?.message}
          />
        )}
      />
      <Controller
        name="targetName"
        control={control}
        rules={{ required: 'חובה למלא את השדה' }}
        render={({ field }) => (
          <TextField
            {...field}
            label="Target Name"
            fullWidth
            required
            error={!!errors.targetName}
            value={field.value?.toString() || ''}
            helperText={errors.targetName?.message}
          />
        )}
      />
      <Controller
        name="targetBankAccount"
        control={control}
        rules={{ required: 'חובה למלא את השדה' }}
        render={({ field }) => (
          <TextField
            {...field}
            label="Target Bank Account"
            fullWidth
            required
            error={!!errors.targetBankAccount}
            value={field.value?.toString() || ''}
            helperText={errors.targetBankAccount?.message}
          />
        )}
      />
      <Controller
        name="phoneNumber"
        control={control}
        rules={{
          required: 'חובה למלא את השדה',
          validate: (value) => {
            if (!value) return 'חובה למלא את השדה';
            const formatted = formatIsraeliPhoneNumber(value);
            if (formatted.length !== 13) {
              return 'מספר טלפון לא תקין';
            }
            return true;
          },
        }}
        render={({ field }) => (
          <TextField
            {...field}
            label="מספר טלפון "
            fullWidth
            required
            error={!!errors.amount}
            value={field.value?.toString() || ''}
            helperText={errors.amount?.message}
          />
        )}
      />
      <Button
        type="submit"
        loading={loading}
        sx={{
          backgroundColor: '#B6F829',
          borderRadius: '15px',
          ':hover': { backgroundColor: '#B6F829' },
          width: '100%',
        }}
      >
        <Typography
          fontSize="18px"
          fontWeight="700"
          color="black"
          fontFamily={'AlmoniDL'}
        >
          שליחת לינק
        </Typography>
      </Button>
    </Stack>
  );
};

export default Feezback;